import { Cpu, Network, Sparkles, Headphones } from "lucide-react";
import { IMG } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import { StatCounter } from "@/components/StatCounter";
import { SectionHeader } from "@/components/SectionHeader";

const PILLARS = [
  { icon: Cpu, title: "Technology", body: "Delivering intelligent living through world-class automation platforms." },
  { icon: Network, title: "Distribution", body: "Building a trusted nationwide network of premium partners." },
  { icon: Sparkles, title: "Design", body: "Curating products that elevate modern living." },
  { icon: Headphones, title: "Support", body: "Providing technical expertise and project guidance at every stage." },
];

export default function About() {
  return (
    <>
      {/* Page hero */}
      <section className="relative pt-40 pb-24 md:pb-32" data-testid="about-hero">
        <div className="container-edge grid lg:grid-cols-12 gap-12 lg:gap-20 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> About Us</p>
            <h1 className="display-1 mt-6">
              Creating <span className="italic text-gold">intelligent spaces</span> for modern living.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Jain Interior Solutions is a premium technology and lifestyle company based in Coimbatore,
              India. We combine world-class home automation technologies with carefully selected interior
              materials and hardware solutions to create sophisticated living experiences.
            </p>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed">
              As the authorized distributor of Fibaro Home Automation in India, we are actively developing
              a nationwide network of partners while supporting homeowners, architects, interior designers,
              builders and system integrators across the country.
            </p>
          </div>
        </div>
      </section>

      {/* Feature image */}
      <section className="container-edge" data-testid="about-image">
        <Reveal>
          <div className="relative aspect-[21/9] overflow-hidden">
            <img src={IMG.about} alt="Premium experience" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/30" />
          </div>
        </Reveal>
      </section>

      {/* Pillars */}
      <section className="py-24 md:py-32" data-testid="pillars-section">
        <div className="container-edge">
          <Reveal>
            <SectionHeader eyebrow="Our Pillars" title={<>Four foundations of <span className="italic text-gold">excellence</span>.</>} />
          </Reveal>
          <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 100} className="bg-background p-10 hover:bg-secondary/60 transition-colors group">
                <p.icon className="h-7 w-7 text-gold" />
                <h3 className="font-display text-2xl mt-8">{p.title}</h3>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                <div className="mt-10 h-px w-8 bg-gold transition-all duration-500 group-hover:w-full" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 md:py-32 bg-charcoal-900 text-white relative overflow-hidden" data-testid="about-stats">
        <div className="absolute inset-0 grain-overlay opacity-30" />
        <div className="container-edge relative grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16">
          <StatCounter value={42} suffix="+" label="Cities Served" />
          <StatCounter value={65} suffix="+" label="Dealer Partners" />
          <StatCounter value={280} suffix="+" label="Projects Supported" />
          <StatCounter value={18} suffix="+" label="Years of Experience" />
        </div>
      </section>
    </>
  );
}
